import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import { addDays, isValid, parseISO, startOfDay } from 'date-fns';

import api from '~/services/api';
import { useStoreActions, useStoreState } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error';
import Modal from '~/ui/components/common/Modal';
import Button from '~/ui/components/common/Button';
import DatePicker from '~/ui/components/inputs/DatePicker';
import Textarea from '~/ui/components/inputs/Textarea';
import { getInitialValues, validate } from './form';
import { IPauseMonitoringFormValues } from './types';
import { IPatientMonitoringPauseBaseDto } from '~/services/api/patientMonitoringPauses/types';

import styles from './PauseMonitoringModal.module.scss';

interface IProps {
  onClose: () => void;
}

const toDateOnly = (value: string): string => value.slice(0, 10);

const PauseMonitoringModal = ({ onClose }: IProps): ReactElement => {
  const [isProcessing, setIsProcessing] = useState(false);

  const patientInfo = useStoreState(store => store.patient.info);
  const currentMonitoringPause = patientInfo?.currentMonitoringPause;

  const { onGetInfo } = useStoreActions(actions => actions.patient);
  const { showError, showNotify } = useStoreActions(actions => actions.snackbar);

  const initialValues = useMemo(
    () => getInitialValues(currentMonitoringPause),
    [
      currentMonitoringPause?.startDate,
      currentMonitoringPause?.endDate,
      currentMonitoringPause?.reason,
    ],
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<IPauseMonitoringFormValues>({
    resolver: validate,
    defaultValues: initialValues,
  });

  const startDate = watch('startDate');
  const today = useMemo(() => startOfDay(new Date()), []);
  const endDateMin = useMemo(() => {
    if (!startDate) return today;

    const startDateValue = parseISO(startDate);

    if (!isValid(startDateValue)) return today;

    const startDatePlusOneDay = addDays(startDateValue, 1);
    return startDatePlusOneDay > today ? startDatePlusOneDay : today;
  }, [startDate, today]);

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  const handlePauseMonitoringSubmit = async (values: IPauseMonitoringFormValues) => {
    if (!patientInfo?.id) return;

    try {
      setIsProcessing(true);

      const payload: IPatientMonitoringPauseBaseDto = {
        startDate: toDateOnly(values.startDate),
        endDate: toDateOnly(values.endDate),
        reason: values.reason,
      };

      if (currentMonitoringPause) {
        await api.patientMonitoringPauses.update({ id: currentMonitoringPause.id, ...payload });
        showNotify('Pause monitoring updated');
      } else {
        await api.patientMonitoringPauses.create({ patientId: patientInfo.id, ...payload });
        showNotify('Monitoring paused');
      }

      await onGetInfo(patientInfo.id);
      onClose();
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (isProcessing) return;

    onClose();
  };

  return (
    <Modal
      open
      maxWidth="sm"
      title="Pause monitoring"
      className={styles.modal}
      onClose={handleClose}
    >
      <div className={styles.inner}>
        <form noValidate onSubmit={handleSubmit(handlePauseMonitoringSubmit)}>
          <Grid container spacing={4}>
            <Grid item xs={6}>
              <DatePicker
                name="startDate"
                label="Start Date"
                required
                control={control}
                errors={errors}
                minDate={today}
                isDateOnly
                placeholder=".. /.. /.."
                isInBody
              />
            </Grid>
            <Grid item xs={6}>
              <DatePicker
                name="endDate"
                label="End Date"
                required
                control={control}
                errors={errors}
                minDate={endDateMin}
                isDateOnly
                placeholder=".. /.. /.."
                isInBody
              />
            </Grid>
            <Grid item xs={12}>
              <Textarea
                name="reason"
                label="Reason for pause"
                placeholder="e.g. Hospitalization, vacation, patient request..."
                required
                register={register}
                errors={errors}
                rows={3}
              />
            </Grid>
          </Grid>

          <div className={styles.infoBlock}>
            A notification reminder will be triggered when the end date is reached.
          </div>

          <div className={styles.btnWrapper}>
            <Button
              variant="outlined"
              color="default"
              disabled={isProcessing}
              onClick={handleClose}
            >
              Cancel
            </Button>
            <div className={styles.widthSpacer} />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              isLoading={isProcessing}
              disabled={isProcessing}
            >
              Save
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default PauseMonitoringModal;
